/**
 * 五子棋游戏组件
 * 统一布局结构，参考井字棋设计
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  GameState,
  Position,
  Move,
  Player,
  GameMode,
  Difficulty,
  GameType
} from '../types/game';
import { createInitialGameState, makeMove, undoMove } from '../utils/gameLogic';
import { getAIDecision } from '../utils/ai';
import GameBoard from './GameBoard';
import GameShareButton from './GameShareButton';

interface GameProps {
  gameMode: GameMode;
  difficulty?: Difficulty;
  gameType: GameType;
  onBackToMenu?: () => void;
  onBackToModeSelect?: () => void;
}

const Game: React.FC<GameProps> = ({
  gameMode,
  difficulty = 'medium',
  gameType,
  onBackToMenu,
  onBackToModeSelect
}) => {
  const [gameState, setGameState] = useState<GameState>(() =>
    createInitialGameState(gameMode, difficulty)
  );
  const [showEndGameDialog, setShowEndGameDialog] = useState(false);
  const [dialogManuallyClosed, setDialogManuallyClosed] = useState(false);
  const [showRules, setShowRules] = useState(false);
  const isAIProcessingRef = useRef(false);

  // 处理格子点击
  const handleCellClick = useCallback((position: Position) => {
    if (gameState.status !== 'playing' || gameState.isAIThinking || isAIProcessingRef.current) {
      return;
    }

    // 如果是AI模式且当前玩家是AI，则不允许点击
    if (gameMode === 'pvc' && gameState.currentPlayer === 'white') {
      return;
    }

    // 执行移动
    const newGameState = makeMove(gameState, position);
    setGameState(newGameState);
  }, [gameState, gameMode]);

  // AI移动处理
  const handleAIMove = useCallback(async () => {
    // 检查是否应该执行AI移动（使用ref防止重复调用）
    if (gameMode !== 'pvc' || gameState.currentPlayer !== 'white' ||
        gameState.status !== 'playing' || isAIProcessingRef.current) {
      return;
    }

    // 设置AI处理标志
    isAIProcessingRef.current = true;
    setGameState(prev => ({ ...prev, isAIThinking: true }));

    try {
      // 模拟AI思考时间
      await new Promise(resolve => setTimeout(resolve, 500));

      // 获取AI决策
      const aiDecision = getAIDecision(gameState, difficulty === 'easy' ? 1 : difficulty === 'medium' ? 2 : 3);

      if (aiDecision.move) {
        // 执行AI移动
        const newGameState = makeMove(gameState, aiDecision.move);
        setGameState(newGameState);
      }
    } catch (error) {
      console.error('AI移动错误:', error);
    } finally {
      isAIProcessingRef.current = false;
      setGameState(prev => ({ ...prev, isAIThinking: false }));
    }
  }, [gameMode, gameState, difficulty]);

  // 悔棋功能
  const handleUndo = useCallback(() => {
    if (gameState.moves.length > 0 && gameState.status === 'playing' && !gameState.isAIThinking) {
      const newGameState = undoMove(gameState);
      setGameState(newGameState);
    }
  }, [gameState]);

  // 重新开始游戏
  const handleRestart = useCallback(() => {
    isAIProcessingRef.current = false;
    setGameState(createInitialGameState(gameMode, difficulty));
    setShowEndGameDialog(false);
    setDialogManuallyClosed(false);
  }, [gameMode, difficulty]);

  // 监听游戏结束
  useEffect(() => {
    if (gameState.status === 'finished' && !showEndGameDialog && !dialogManuallyClosed) {
      setShowEndGameDialog(true);
    }
  }, [gameState.status, gameState.winner, showEndGameDialog, dialogManuallyClosed]);

  // AI移动触发
  useEffect(() => {
    // 只在轮到AI且游戏正在进行时触发AI移动
    if (gameMode === 'pvc' && gameState.currentPlayer === 'white' &&
        gameState.status === 'playing' && !gameState.isAIThinking && !isAIProcessingRef.current) {
      handleAIMove();
    }
  }, [gameState.currentPlayer, gameMode, gameState.status, gameState.isAIThinking, handleAIMove]);

  // 游戏结束对话框
  const EndGameDialog = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-8 max-w-md w-full mx-4 shadow-2xl relative">
        {/* 关闭按钮 */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            setShowEndGameDialog(false);
            setDialogManuallyClosed(true);
          }}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
          title="关闭"
        >
          ×
        </button>

        <h2 className="text-2xl font-bold text-center mb-4">
          {gameState.winner ? (
            <span className="text-green-600">
              {gameState.winner === 'black' ? '黑方' : '白方'}获胜！
            </span>
          ) : (
            <span className="text-yellow-600">平局！</span>
          )}
        </h2>

        <div className="text-center text-gray-600 mb-6">
          <p>游戏统计：</p>
          <p>总步数: {gameState.moves.length}</p>
          <p>棋盘: 15×15</p>
          {gameMode === 'pvc' && (
            <p>AI难度: {difficulty === 'easy' ? '简单' : difficulty === 'medium' ? '中等' : '困难'}</p>
          )}
        </div>

        <div className="space-y-3">
          <button
            onClick={handleRestart}
            className="w-full bg-blue-500 text-white px-6 py-3 rounded-lg hover:bg-blue-600 transition-colors font-medium"
          >
            再来一局
          </button>
          <button
            onClick={onBackToMenu}
            className="w-full bg-gray-500 text-white px-6 py-3 rounded-lg hover:bg-gray-600 transition-colors font-medium"
          >
            返回主页
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex flex-col">
      {/* 游戏头部 - 统一布局 */}
      <div className="bg-white shadow p-2">
        {/* 标题区域 - 手机端独立行，桌面端居中 */}
        <div className="text-center mb-2 sm:mb-0">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-center sm:space-x-0 sm:space-y-1 space-y-1">
            {/* 美化标题 */}
            <div className="flex items-center justify-center space-x-2">
              <span className="text-2xl">⚫</span>
              <h1 className="text-base sm:text-lg font-bold bg-gradient-to-r from-gray-800 via-gray-700 to-gray-600 bg-clip-text text-transparent sm:mb-1">
                五子棋
              </h1>
              <span className="text-2xl">⚪</span>
            </div>

            {/* 美化模式描述 */}
            <div className="flex items-center justify-center space-x-2">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-500 to-purple-500 text-white">
                <span className="w-2 h-2 mr-1 bg-white rounded-full animate-pulse"></span>
                {gameMode === 'pvp' ? '双人对战' : `人机对战`}
              </span>

              {gameMode === 'pvc' && (
                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                  difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                  difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                  'bg-red-100 text-red-800'
                }`}>
                  {difficulty === 'easy' ? '😊 简单' : difficulty === 'medium' ? '🎯 中等' : '🔥 困难'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* 控制按钮区域 */}
        <div className="flex items-center justify-between">
          {/* 左侧按钮组 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            {/* 首页按钮 */}
            {onBackToMenu && (
              <button
                onClick={onBackToMenu}
                className="flex items-center space-x-1 px-2 py-1.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-xs font-medium"
                title="回到首页"
              >
                <span>🏠</span>
                <span className="hidden sm:inline">首页</span>
              </button>
            )}

            {/* 返回按钮 */}
            {onBackToModeSelect && (
              <button
                onClick={onBackToModeSelect}
                className="flex items-center space-x-1 px-2 py-1.5 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors text-xs font-medium"
                title="返回模式选择"
              >
                <span>🔙</span>
                <span className="hidden sm:inline">返回</span>
              </button>
            )}

            {/* 重新开始按钮 */}
            <button
              onClick={handleRestart}
              className="flex items-center space-x-1 px-2 py-1.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors text-xs font-medium"
              title="重新开始"
            >
              <span>🔄</span>
              <span className="hidden sm:inline">重开</span>
            </button>

            {/* 悔棋按钮 */}
            <button
              onClick={handleUndo}
              disabled={gameState.moves.length === 0}
              className={`flex items-center space-x-1 px-2 py-1.5 rounded-lg transition-colors text-xs font-medium ${
                gameState.moves.length > 0
                  ? 'bg-blue-500 text-white hover:bg-blue-600'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }`}
              title="悔棋"
            >
              <span>↩️</span>
              <span className="hidden sm:inline">悔棋</span>
            </button>
          </div>

          {/* 右侧控制按钮 */}
          <div className="flex items-center space-x-1 sm:space-x-1">
            {/* 游戏规则按钮 */}
            <button
              onClick={() => setShowRules(true)}
              className="flex items-center space-x-1 px-2 py-1.5 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-xs font-medium"
              title="游戏规则"
            >
              <span>📖</span>
              <span className="hidden sm:inline">规则</span>
            </button>

            {/* 分享按钮 */}
            <GameShareButton gameType={gameType} />
          </div>
        </div>
      </div>

      {/* 游戏状态信息 - 统一布局 */}
      <div className="bg-white shadow p-2">
        <div className="flex items-center justify-between">
          {/* 当前玩家信息 */}
          <div className="flex items-center space-x-2">
            <div className={`w-4 h-4 rounded-full ${
              gameState.currentPlayer === 'black' ? 'bg-gray-900' : 'bg-gray-100 border-2 border-gray-400'
            }`}></div>
            <span className="text-sm sm:text-base font-medium">
              {gameState.currentPlayer === 'black' ? '黑方回合' : '白方回合'}
            </span>
            {gameState.isAIThinking && (
              <span className="text-xs sm:text-sm text-blue-600 animate-pulse ml-2">AI思考中...</span>
            )}
          </div>

          {/* 步数信息 */}
          <div className="flex items-center space-x-3 text-sm sm:text-base text-gray-600">
            <span>步数: {gameState.moves.length}</span>
          </div>
        </div>
      </div>

      {/* 棋盘 - 移动端满屏布局 */}
      <div className="flex-1 flex items-center justify-center px-1 py-1 xs:px-1 xs:py-2">
        <div className="w-full h-full max-w-none">
          <GameBoard
            board={gameState.board}
            onCellClick={handleCellClick}
            disabled={gameState.status !== 'playing' || gameState.isAIThinking}
            lastMove={gameState.moves.length > 0 ? gameState.moves[gameState.moves.length - 1].position : undefined}
          />
        </div>
      </div>

      {/* 游戏规则提示 - 统一布局 */}
      <div className="mt-10 bg-blue-50 border-t border-blue-200 p-2 sm:p-3">
        <p className="text-sm sm:text-base text-blue-800 text-center leading-relaxed">
          <strong>规则：</strong>
          15×15棋盘，五子连线获胜 | 黑方先行 | {gameMode === 'pvp' ? '双人对战' : '人机对战'}
        </p>
      </div>

      {/* 游戏规则对话框 */}
      {showRules && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl p-6 max-w-4xl w-full max-h-[80vh] overflow-y-auto relative">
            {/* 右上角关闭按钮 */}
            <button
              onClick={() => setShowRules(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors text-2xl font-bold w-8 h-8 flex items-center justify-center rounded-full hover:bg-gray-100"
              title="关闭"
            >
              ×
            </button>

            <div className="text-center mb-6 pr-8">
              <h2 className="text-2xl font-bold mb-4 text-gray-800">五子棋游戏规则</h2>
              <div className="text-6xl mb-4">⚫</div>
            </div>

            <div className="space-y-4 text-gray-700">
              {/* 游戏简介 */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-blue-800">📖 游戏简介</h3>
                <p className="text-sm leading-relaxed">
                  五子棋（Gomoku/Five-in-a-Row）是一种源自中国的两人对弈棋类游戏。
                  游戏规则简单易懂，但策略丰富，深受世界各地玩家喜爱。
                  在国际象棋、围棋之外，五子棋是最普及的棋类游戏之一。
                </p>
              </div>

              {/* 游戏设置 */}
              <div className="bg-green-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-green-800">🎯 游戏设置</h3>
                <ul className="text-sm space-y-1">
                  <li><strong>棋盘：</strong>15×15 方格棋盘</li>
                  <li><strong>棋子：</strong>黑色和白色圆形棋子</li>
                  <li><strong>先行方：</strong>黑方先行，双方轮流下棋</li>
                  <li><strong>下棋位置：</strong>棋盘的任意空格位置</li>
                </ul>
              </div>

              {/* 游戏规则 */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-purple-800">🎮 游戏规则</h3>
                <ul className="text-sm space-y-1">
                  <li><strong>轮流下棋：</strong>黑方先行，之后双方轮流在空格上放置棋子</li>
                  <li><strong>放置规则：</strong>每次只能在一个空格上放置一枚棋子</li>
                  <li><strong>不可移动：</strong>棋子一旦放置，不能移动或移除</li>
                  <li><strong>禁手规则：</strong>在某些规则变体中，黑方有三三禁手、四四禁手等限制</li>
                </ul>
              </div>

              {/* 获胜条件 */}
              <div className="bg-indigo-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-indigo-800">🏆 获胜条件</h3>
                <ul className="text-sm space-y-1">
                  <li><strong>五子连线：</strong>率先在棋盘上形成五子连线（横、竖、斜方向均可）的玩家获胜</li>
                  <li><strong>胜利判定：</strong>只要形成连续的五颗同色棋子即为获胜</li>
                  <li><strong>平局条件：</strong>棋盘下满但无人形成五子连线则为平局</li>
                </ul>
              </div>

              {/* 策略提示 */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-gray-800">💡 策略提示</h3>
                <ul className="text-sm space-y-1">
                  <li>• 占据中心位置具有战略优势，可以向多个方向发展</li>
                  <li>• 同时形成多条潜在连线，增加获胜机会</li>
                  <li>• 注意防守，及时阻止对手形成四子连线</li>
                  <li>• 构建双三或三四组合，制造无法防守的局面</li>
                  <li>• 学会识别威胁点，提前布局攻防转换</li>
                </ul>
              </div>

              {/* 技能系统说明 */}
              <div className="bg-amber-50 p-4 rounded-lg">
                <h3 className="font-bold text-lg mb-2 text-amber-800">✨ 技能系统</h3>
                <ul className="text-sm space-y-1">
                  <li><strong>能量系统：</strong>每位玩家拥有能量值，用于使用技能</li>
                  <li><strong>技能获取：</strong>游戏开始时随机获得3个技能</li>
                  <li><strong>技能效果：</strong>包括移动棋子、阻挡位置、额外回合等</li>
                  <li><strong>冷却机制：</strong>使用技能后需要等待一定回合数才能再次使用</li>
                </ul>
              </div>
            </div>

            <div className="flex justify-center mt-6">
              <button
                onClick={() => setShowRules(false)}
                className="px-6 py-3 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors font-medium"
              >
                我知道了
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 游戏结束对话框 */}
      {showEndGameDialog && <EndGameDialog />}
    </div>
  );
};

export default Game;